import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Linking } from 'react-native';
import {
  PHONE_CONTACTS_SUPPORTED,
  checkContactsPermission,
  loadPhoneContacts,
  requestContactsPermission,
  type ContactsPermission,
} from '../services/phoneContacts';
import type { PickerContact } from '../utils/customerPicker';

export type PhoneContactsStatus =
  | 'unavailable'
  | 'checking'
  | 'undetermined'
  | 'denied'
  | 'blocked'
  | 'loading'
  | 'ready'
  | 'error';

// Read once per app session: the address book is slow to read and rarely
// changes while an order is being saved. `reload` reads it again.
let cachedContacts: PickerContact[] | null = null;

const statusForPermission = (
  permission: ContactsPermission,
): PhoneContactsStatus => (permission === 'granted' ? 'loading' : permission);

/**
 * The phone's contacts for the customer picker, read only while `active`
 * (the sheet is open). Asks for permission on `request`; when the person
 * comes back from the phone's settings, checks again on its own.
 */
export const usePhoneContacts = (active: boolean) => {
  const [status, setStatus] = useState<PhoneContactsStatus>(
    PHONE_CONTACTS_SUPPORTED ? 'checking' : 'unavailable',
  );
  const [contacts, setContacts] = useState<PickerContact[]>(
    cachedContacts ?? [],
  );
  const mountedRef = useRef(true);
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const list = await loadPhoneContacts();
      cachedContacts = list;
      if (!mountedRef.current) return;
      setContacts(list);
      setStatus('ready');
    } catch (error) {
      console.log('loadPhoneContacts error', error);
      if (mountedRef.current) setStatus('error');
    }
  }, []);

  const check = useCallback(async () => {
    if (cachedContacts) {
      setContacts(cachedContacts);
      setStatus('ready');
      return;
    }
    const permission = await checkContactsPermission();
    if (!mountedRef.current) return;
    if (permission === 'granted') {
      await load();
    } else {
      setStatus(statusForPermission(permission));
    }
  }, [load]);

  useEffect(() => {
    if (!active || !PHONE_CONTACTS_SUPPORTED) return;
    void check();
  }, [active, check]);

  // Back from Settings with access turned on: pick the contacts up.
  useEffect(() => {
    if (!active || !PHONE_CONTACTS_SUPPORTED) return;
    const subscription = AppState.addEventListener('change', state => {
      if (state !== 'active') return;
      const current = statusRef.current;
      if (current === 'blocked' || current === 'denied') void check();
    });
    return () => subscription.remove();
  }, [active, check]);

  const request = useCallback(async () => {
    const permission = await requestContactsPermission();
    if (!mountedRef.current) return;
    if (permission === 'granted') {
      await load();
    } else {
      setStatus(statusForPermission(permission));
    }
  }, [load]);

  const reload = useCallback(() => {
    cachedContacts = null;
    void load();
  }, [load]);

  const openSettings = useCallback(() => {
    Linking.openSettings().catch(() => {});
  }, []);

  return { status, contacts, request, reload, openSettings } as const;
};

export default usePhoneContacts;
